import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/da225g3k8r5te5mqse3g";

type FormState = "idle" | "submitting" | "success" | "error";

export default function Cta() {
  const [status, setStatus] = useState<FormState>("idle");
  const [message, setMessage] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("phone_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("订阅成功，感谢你的关注！");
      form.reset();
      return;
    }

    const email = String(formData.get("email") || "");
    if (!email) {
      setStatus("error");
      setMessage("请输入有效的邮箱地址。");
      return;
    }

    setStatus("submitting");
    setMessage("");

    const body = new URLSearchParams();
    body.set("email", email);

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam|form data is spam/i.test(serverMsg);

      if (res.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("订阅成功，最新更新不错过！");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "订阅失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，请稍后重试。");
    }
  }

  return (
    <section className="relative overflow-hidden py-16 md:py-24">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://readdy.ai/api/search-image?query=Warm%20abstract%20illustration%20of%20floating%20open%20books%20and%20paper%20planes%20in%20coral%20orange%20and%20golden%20amber%20tones%2C%20soft%20cream%20background%2C%20dreamy%20storytelling%20atmosphere%2C%20clean%20artistic%20composition&width=1600&height=700&seq=cta-banner&orientation=landscape')",
        }}
      ></div>
      <div className="absolute inset-0 bg-gradient-to-r from-background-950/80 to-background-800/60"></div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-16">
          <div>
            <h2 className="font-heading text-3xl text-background-50 md:text-4xl">
              现在订阅，追更不断档
            </h2>
            <p className="mt-3 max-w-lg text-background-100">
              订阅尾巴漫画网，第一时间获取新作上架与更新提醒。带上你的阅读书签，
              在任何设备继续上次的冒险。
            </p>
            <a
              href="#recommend"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-7 py-3.5 text-base font-medium text-accent-950 transition-colors hover:bg-accent-600"
            >
              去选一部好漫画
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>

          <div className="rounded-2xl bg-background-50 p-6 md:p-8">
            <h3 className="font-heading text-xl text-foreground-950">订阅更新提醒</h3>
            <p className="mt-1 text-sm text-foreground-600">留下邮箱，不错过任何一部好故事。</p>

            <form className="mt-5" data-readdy-form onSubmit={handleSubmit} noValidate>
              <div className="flex flex-col gap-3 sm:flex-row">
                <label className="sr-only" htmlFor="cta-email">
                  邮箱地址
                </label>
                <input
                  id="cta-email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  placeholder="请输入邮箱地址"
                  className="w-full flex-1 rounded-full border border-background-300 bg-background-100 px-5 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "submitting" ? "提交中..." : "订阅"}
                </button>
              </div>

              <input
                type="text"
                name="phone_alt"
                aria-hidden="true"
                autoComplete="off"
                tabIndex={-1}
                readOnly
                className="form-hp-field"
              />

              {status === "success" && (
                <p className="mt-3 flex items-center gap-2 text-sm text-secondary-700">
                  <i className="ri-checkbox-circle-line"></i>
                  {message}
                </p>
              )}
              {status === "error" && (
                <p className="mt-3 flex items-center gap-2 text-sm text-primary-700">
                  <i className="ri-error-warning-line"></i>
                  {message}
                </p>
              )}
            </form>

            <p className="mt-4 text-xs text-foreground-500">
              提交即表示你同意接收尾巴漫画网的更新邮件，可随时退订。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}